import React, { useState, useEffect, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import images from "../../../assets/images";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { usePermissions } from "../../../hooks/usePermission";
import { PREVENTIVE_PREDICTIVE_MAINTENANCE } from "../../../constants/modules";
import { MaintenanceSchedule } from "../../../types/api";
import MaintenanceScheduleForm from "./MaintenanceScheduleForm";
import { useOrgUsersStore } from "../../../store/store";
import { getAssetInventory } from "../../../service/api";
import { usePreventiveMaintenanceStore } from "../../../store/utilitySystemsStore";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const MaintenanceScheduleList: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceSchedule | null>(null);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [assets, setAssets] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const {
    maintenanceSchedules,
    schedulesLoading,
    fetchMaintenanceSchedules,
    deleteMaintenanceSchedule,
    searchMaintenanceSchedules,
  } = usePreventiveMaintenanceStore();

  const fetchAssets = useCallback(async () => {
    try {
      const response = await getAssetInventory();
      if (response.data?.results) {
        setAssets(response.data.results);
      } else if (response.data?.message) {
        setAssets(Array.isArray(response.data.message) ? response.data.message : []);
      } else if (response.data?.data) {
        setAssets(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error("Error fetching asset inventory:", error);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      await fetchMaintenanceSchedules();
    } catch (error: any) {
      toast.error("Failed to fetch maintenance schedules");
      console.error("Error fetching schedules:", error);
    }
  }, [fetchMaintenanceSchedules]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      fetchSchedules();
      return;
    }

    try {
      await searchMaintenanceSchedules(searchTerm);
    } catch (error: any) {
      toast.error("Search failed");
      console.error("Error searching schedules:", error);
    }
  }, [searchTerm, fetchSchedules, searchMaintenanceSchedules]);

  useEffect(() => {
    fetchOrgUsers();
    fetchSchedules();
    fetchAssets();
  }, [fetchOrgUsers, fetchSchedules, fetchAssets]);

  useEffect(() => {
    if (searchTerm) {
      handleSearch();
    } else {
      fetchSchedules();
    }
  }, [searchTerm, handleSearch, fetchSchedules]);

  const handleCreate = () => {
    setSelectedSchedule(null);
    setMode("create");
    setIsFormOpen(true);
  };

  const handleEdit = (schedule: MaintenanceSchedule) => {
    setSelectedSchedule(schedule);
    setMode("edit");
    setIsFormOpen(true);
  };

  const handleView = (schedule: MaintenanceSchedule) => {
    setSelectedSchedule(schedule);
    setMode("view");
    setIsFormOpen(true);
  };

  const handleDelete = async (schedule: MaintenanceSchedule) => {
    if (!window.confirm("Are you sure you want to delete this maintenance schedule?")) {
      return;
    }

    try {
      const id = schedule.id || schedule._id;
      if (!id) return;

      await deleteMaintenanceSchedule(id);
      toast.success("Maintenance schedule deleted successfully");
      fetchSchedules();
    } catch (error: any) {
      toast.error("Failed to delete maintenance schedule");
      console.error("Error deleting schedule:", error);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedSchedule(null);
    setMode("create");
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchSchedules();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const getAssetName = (assetId: string): string => {
    const asset = assets.find((a) => (a._id || a.id) === assetId);
    return asset?.name || asset?.equipmentName || assetId;
  };

  const getTechnicianName = (technicianId: string): string => {
    if (!technicianId || !orgUsersData?.length) return technicianId || "N/A";
    const user = orgUsersData.find((u: any) => (u._id || u.id) === technicianId);
    if (!user) return technicianId;
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || user.email || technicianId;
  };

  const columnDefs: ColDef<MaintenanceSchedule>[] = [
    {
      headerName: "Asset ID",
      field: "assetId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 5px" },
      cellRenderer: (params: any) => {
        return getAssetName(params.value);
      },
    },
    {
      headerName: "Frequency",
      field: "frequency",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const freq = params.value || "";
        return freq.charAt(0).toUpperCase() + freq.slice(1);
      },
    },
    {
      headerName: "Next Due Date",
      field: "nextDueDate",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Technician",
      field: "technicianName",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        return params.data.technicianName || getTechnicianName(params.data.technicianId);
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "black";
        switch (params.value) {
          case "Active":
            color = "#4CAF50";
            break;
          case "Inactive":
            color = "#9E9E9E";
            break;
          case "Suspended":
            color = "#F44336";
            break;
        }
        return {
          color,
          fontWeight: "bold",
          padding: "10px 0 0 0",
        };
      },
    },
    {
      headerName: "Action",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const schedule = params.data;

        return (
          <div className="flex items-center gap-2 justify-center">
            <button
              onClick={() => handleView(schedule)}
              title="View Schedule"
            >
              <img className="iconTd" src={images.viewIcon} alt="View" />
            </button>

            {canUpdate(PREVENTIVE_PREDICTIVE_MAINTENANCE) && (
              <button
                onClick={() => handleEdit(schedule)}
                title="Edit Schedule"
              >
                <img className="iconTd" src={images.editIcon} alt="Edit" />
              </button>
            )}

            {canDelete(PREVENTIVE_PREDICTIVE_MAINTENANCE) && (
              <button
                onClick={() => handleDelete(schedule)}
                title="Delete Schedule"
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          Preventive & Predictive Maintenance
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/utility-systems/maintenance/logs")}
            className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
          >
            <FileText size={18} />
            Maintenance Logs
          </button>
          {canCreate(PREVENTIVE_PREDICTIVE_MAINTENANCE) && (
            <button
              className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-lg hover:bg-[#0d8f8c] transition-colors"
              onClick={handleCreate}
            >
              <Plus size={18} />
              Create Schedule
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <Dropdown
          selectedValue={selectedRegion}
          onChange={setSelectedRegion}
          type="region"
        />
        <Dropdown
          selectedValue={selectedPremises}
          onChange={setSelectedPremises}
          type="premises"
          selectedRegion={selectedRegion}
        />
      </div>

      <div className="bg-[#ffffff] w-[100%]">
        <div className="px-3 py-3">
          <div className="relative flex justify-between items-center">
            <div className="flex w-10/12 justify-start items-center">
              <Search className="absolute left-3 text-gray-500" />
              <input
                type="text"
                placeholder="Search by Asset ID, Frequency, or Technician"
                className="border-2 border-[#4f5762] mr-5 w-[300px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="ag-theme-alpine mt-4">
          {schedulesLoading ? (
            <div className="text-center py-4">Loading schedules...</div>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={maintenanceSchedules}
              columnDefs={columnDefs}
              rowHeight={50}
              headerHeight={65}
              paginationPageSize={10}
              pagination={true}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={(params) => {
                return {
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginBottom: "8px",
                  borderRadius: "4px",
                  backgroundColor: "white",
                };
              }}
            />
          )}
        </div>
      </div>

      {isFormOpen && (
        <MaintenanceScheduleForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          schedule={selectedSchedule}
          mode={mode}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default MaintenanceScheduleList;

